import type { RoutesNamesList } from '@typed-router';

export default defineNuxtPlugin(() => {
  const { currentRouteName, goToLocaleRoute } = useRoutes();
  const { isAuthenticated, isRedirectingHome } = storeToRefs(useAuthStore());
  const { updateWallet, walletId } = useWallet();

  // busca carteira quando entra
  if (isAuthenticated.value && walletId.value === 0) {
    updateWallet();
  }

  const routesAlwaysAllowed: RoutesNamesList[] = [
    'signup',
    'help',
    'contact',
    'pending',
    'maintenance',
    'migrate-confirm',
    'page-term',
  ];

  function isAnRouteAlwaysAllowed(routeName: RoutesNamesList) {
    return routesAlwaysAllowed.includes(routeName);
  }

  const restrictedRoutesCassinoGame: RoutesNamesList[] = [
    'games-provider-game',
  ];

  function isAnRestrictedRouteCasinoGame(routeName: RoutesNamesList) {
    return restrictedRoutesCassinoGame.includes(routeName);
  }

  const restrictedRoutesCassinoList: RoutesNamesList[] = [
    'games',
    'games-live',
    'games-category-category',
    'games-providers',
  ];

  function isAnRestrictedRouteCasinoList(routeName: RoutesNamesList) {
    return restrictedRoutesCassinoList.includes(routeName);
  }

  const restrictedRoutesSports: RoutesNamesList[] = ['sports-sports'];

  function isAnRestrictedRouteSports(routeName: RoutesNamesList) {
    return restrictedRoutesSports.includes(routeName);
  }

  const restrictedRoutesToWithdrawOnly: RoutesNamesList[] = [
    'index',
    'user-wallet',
    'user-wallet-transactions',
    'user-account-protection',
    'user-account',
    'user-security',
    'user-login-history',
    'user-history-casino',
    'user-history-sports',
    'user-history-poker',
    'user-history-full',
    'user-validate-type-token',
    'migrate-confirm',
    'pending',
    'signup',
    'help',
    'contact',
    'recover',
    'sponsors',
    'page-term',
    'user-irpf',
    'user-irpf-rendimentos',
  ];

  function isAnOnlyWithdrawRoute(routeName: RoutesNamesList) {
    return restrictedRoutesToWithdrawOnly.includes(routeName);
  }

  const protectedRouteNames: RoutesNamesList[] = [
    'user-account',
    'user-security',
    'user-login-history',
    'user-account-protection',
    'user-wallet',
    'user-wallet-transactions',
    'user-wallet-last-withdraw',
    'user-history-full',
    'user-history-casino',
    'user-history-sports',
    'user-history-poker',
    'user-notifications',
    'user-refers',
    'user-rewards',
    'user-validate-type-token',
    'user-profile',
    'user-irpf',
    'user-irpf-rendimentos',
    'promotions',
    'promotions-promotion',
    'promocode',
    // Área VIP: bloqueada para deslogado. Acesso direto por URL → redirect
    // pra home; navegação interna (menus) → abre o modal de login. Mesmo
    // mecanismo do promocode (auth.global.ts).
    'vip',
    'vip-store',
    'vip-badges',
    'vip-levels',
    'vip-missions',
    'vip-mini-games',
    'vip-tournaments',
    'vip-tournaments-tournament',
  ];

  function isAnUnauthorizedRoute(routeName: RoutesNamesList) {
    return protectedRouteNames.includes(routeName);
  }

  const protectedEndpoints: string[] = [
    '/api/wallet',
    '/api/bonus',
    '/api/users',
    '/api/user',
    '/api/new-withdraws',
    '/api/start-game',
    '/api/betby/get-jwt',
    '/api/auth',
    '/api/pix-keys',
    '/api/mex-bank-accounts',
    '/api/bff/transactions',
    '/api/bff/users',
  ];

  function isAnUnauthorizedEndpoint(url: string) {
    return protectedEndpoints.some((endpoint) => url.includes(endpoint));
  }

  const { logout } = useAuthStore();

  watch(
    isAuthenticated,
    async (isAuthenticated: boolean, oldValue: boolean) => {
      if (oldValue && !isAuthenticated) {
        logout();
      }

      if (!currentRouteName.value) {
        return;
      }

      const isUnauthorizedToProceed = isAnUnauthorizedRoute(
        currentRouteName.value,
      );

      if (!isUnauthorizedToProceed) {
        return;
      }

      if (isAuthenticated) {
        return;
      }

      // Bug real: um logout({redirectToHome: true}) explícito já dispara
      // seu próprio navigateTo('/') — se este watcher disparasse outro
      // pro mesmo lugar em paralelo, o Vue Router cancelava uma navegação
      // pela outra e a página ficava travada (ver comentário em
      // stores/auth.ts isRedirectingHome). Este watcher só precisa agir
      // como safety net para os casos em que o logout NÃO pediu redirect
      // (ex.: sessão expirada via 401, sem `redirectToHome`).
      if (isRedirectingHome.value) {
        return;
      }

      return await goToLocaleRoute({
        name: 'index',
      });
    },
  );

  return {
    provide: {
      auth: {
        isAnRouteAlwaysAllowed,
        isAnRestrictedRouteCasinoGame,
        isAnRestrictedRouteCasinoList,
        isAnRestrictedRouteSports,
        isAnOnlyWithdrawRoute,
        isAnUnauthorizedRoute,
        isAnUnauthorizedEndpoint,
      },
    },
  };
});
